import { useCallback, useState } from 'react';

/** Everything a wizard collects. Flat strings so it round-trips through sessionStorage unchanged. */
export interface OnboardingDraft {
  step: number;
  name: string;
  description: string;
  /** `ProjectGeneratePromptTemplate.label` the description was started from, or '' when none. */
  templateLabel: string;
  websiteUrl: string;
  githubUrl: string;
  docsUrl: string;
  linkedinUrl: string;
  /** Any further public pages, for the source analysis (business validation). */
  otherUrls: string[];
  /** The stock exchange and the ticker symbol (stock validation). */
  exchange: string;
  symbol: string;
}

export type OnboardingUrlField = 'websiteUrl' | 'githubUrl' | 'docsUrl' | 'linkedinUrl';

const EMPTY_DRAFT: OnboardingDraft = {
  step: 0,
  name: '',
  description: '',
  templateLabel: '',
  websiteUrl: '',
  githubUrl: '',
  docsUrl: '',
  linkedinUrl: '',
  otherUrls: [],
  exchange: '',
  symbol: '',
};

function storageKey(wizardKey: string): string {
  return `onboarding_draft_${wizardKey}`;
}

function readDraft(wizardKey: string): OnboardingDraft {
  try {
    const raw = sessionStorage.getItem(storageKey(wizardKey));
    if (raw) return { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<OnboardingDraft>) };
  } catch {
    // corrupt or blocked storage — start fresh
  }
  return EMPTY_DRAFT;
}

/**
 * Keeps the half-filled wizard (inputs and current step) in sessionStorage,
 * one entry per wizard, so a reload or a sign-in round trip does not lose
 * it. Cleared once the project exists.
 */
export function useOnboardingDraft(wizardKey: string) {
  const [draft, setDraft] = useState<OnboardingDraft>(() => readDraft(wizardKey));

  const updateDraft = useCallback(
    (patch: Partial<OnboardingDraft>) => {
      setDraft((prev) => {
        const next = { ...prev, ...patch };
        try {
          sessionStorage.setItem(storageKey(wizardKey), JSON.stringify(next));
        } catch {
          // private browsing / quota — keep the in-memory draft
        }
        return next;
      });
    },
    [wizardKey],
  );

  const clearDraft = useCallback(() => {
    setDraft(EMPTY_DRAFT);
    try {
      sessionStorage.removeItem(storageKey(wizardKey));
    } catch {
      // ignore
    }
  }, [wizardKey]);

  return { draft, updateDraft, clearDraft };
}
